import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog, StatusBadge, toneForStatus } from '@ibe/ui';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { PrimaryCell, RowActions } from '../../../components/resource-list/cells.js';
import { ResourceListPage } from '../../../components/resource-list/resource-list-page.js';
import { type ColumnDescriptor } from '../../../lib/resource-list/columns.js';
import { inclusions, inclusionEndpoints } from '../api/endpoints.js';
import { inclusionRow, type InclusionRow } from '../api/schemas.js';

function formatDate(value: string | undefined): string {
  if (value === undefined) return '';
  return new Date(value).toLocaleDateString('en-AU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatCost(row: InclusionRow): string {
  if (row.unitCost === undefined || row.currency === undefined) return '';
  return `${row.unitCost} ${row.currency}`;
}

const columns: readonly ColumnDescriptor<InclusionRow>[] = [
  {
    id: 'name',
    header: 'Inclusion',
    sortable: true,
    alwaysVisible: true,
    fields: ['name', 'code'],
    cell: (row) => (
      <PrimaryCell
        label={row.name ?? row.id}
        to={`/inclusions/${row.id}`}
        secondary={row.code ?? null}
      />
    ),
  },
  {
    id: 'unitCost',
    header: 'Unit cost',
    sortable: false,
    fields: ['unitCost', 'currency'],
    cell: (row) => formatCost(row),
  },
  {
    id: 'isActive',
    header: 'Status',
    sortable: false,
    fields: ['isActive'],
    cell: (row) => (
      <StatusBadge tone={toneForStatus(row.isActive ? 'ACTIVE' : 'INACTIVE')}>
        {row.isActive ? 'Active' : 'Inactive'}
      </StatusBadge>
    ),
  },
  {
    id: 'description',
    header: 'Description',
    fields: ['description'],
    cell: (row) => row.description,
  },
  {
    id: 'createdAt',
    header: 'Created',
    sortable: true,
    fields: ['createdAt'],
    defaultVisible: false,
    cell: (row) => formatDate(row.createdAt),
  },
  {
    id: 'actions',
    header: '',
    headerLabel: 'Actions',
    align: 'right',
    alwaysVisible: true,
    fields: ['name'],
    cell: (row: InclusionRow): React.JSX.Element => <InclusionRowActions row={row} />,
  },
];

function InclusionRowActions({ row }: { readonly row: InclusionRow }): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const softDelete = inclusions.useSoftDelete();
  const label = row.name ?? row.id;

  return (
    <>
      <div className="flex flex-nowrap items-center justify-end gap-1">
        <RowActions
          actions={[
            { label: `View ${label}`, icon: Eye, to: `/inclusions/${row.id}` },
            { label: `Edit ${label}`, icon: Pencil, to: `/inclusions/${row.id}/edit` },
          ]}
        />
        <PermissionGuard permission={PERMISSIONS.INCLUSION_DELETE}>
          <RowActions
            actions={[
              {
                label: `Delete ${label}`,
                icon: Trash2,
                destructive: true,
                onClick: () => {
                  setConfirming(true);
                },
              },
            ]}
          />
        </PermissionGuard>
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Delete inclusion"
        description={`${label} will be retired from the catalogue. Existing rate plans and history are retained.`}
        confirmLabel="Delete"
        destructive
        loading={softDelete.isPending}
        onConfirm={() => {
          softDelete.mutate(row.id, {
            onSuccess: () => {
              setConfirming(false);
            },
          });
        }}
      />
    </>
  );
}

export function InclusionListPage(): React.JSX.Element {
  return (
    <ResourceListPage
      title="Inclusions"
      description="The optional, chargeable items a rate plan can include."
      path={inclusionEndpoints.list}
      resource="inclusions"
      schema={inclusionRow}
      columns={columns}
      rowKey={(row) => row.id}
      permission={PERMISSIONS.INCLUSION_READ}
      actions={
        <PermissionGuard permission={PERMISSIONS.INCLUSION_CREATE}>
          <Button asChild>
            <Link to="/inclusions/new">
              <Plus className="h-4 w-4" aria-hidden="true" />
              New inclusion
            </Link>
          </Button>
        </PermissionGuard>
      }
    />
  );
}
